import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { mapActions, mapGetters } from "vuex";
import * as types from "@board/store/types";
import * as accountTypes from "@account/store/types";
import { IEDate, uGetDateFormat } from "@appbase/util/date-helper";
import { dispatchEventBus } from "@appbase/util/eventBus";
import localStorageHelper from "@appbase/util/local-storage-helper";
import VueAES from "@appbase/util/vue-aes";
import { getGUID } from "@appbase/util/guid";
import { open, OpenType } from "@board/open-handler";
import api from "@board/api";
import { cache, invalidateUrl, openPopup } from "@appbase/util";
import { ids } from "@board/module";

import { ViewFrame } from "@appbase/components/editor";
import AttachFileField from "@board/components/board/readComponents/AttachFileField.vue";
import CommentBox from "@board/components/board/readComponents/CommentBox.vue";
import RecommendList from "@board/components/board/readComponents/RecommendList.vue";

import TextField from "@board/components/board/readComponents/TextField.vue";
import TextAreaField from "@board/components/board/readComponents/TextAreaField.vue";
import SelectField from "@board/components/board/readComponents/SelectField.vue";
import DateField from "@board/components/board/readComponents/DateField.vue";
import DatetimeField from "@board/components/board/readComponents/DatetimeField.vue";
import TimeSpanField from "@board/components/board/readComponents/TimeSpanField.vue";
import MailSendField from "@board/components/board/readComponents/MailSendField.vue";
import TagField from "@board/components/board/readComponents/TagField.vue";
import SelectUserField from "@board/components/board/readComponents/SelectUserField.vue";
import PasswordCheck from "@board/components/common/PasswordCheck.vue";
import ArticleHistoryList from "@board/components/board/readComponents/ArticleHistoryList.vue";
import OpenWindowField from "@board/components/board/readComponents/OpenWindowField.vue";
import UserCardComponent from "@appbase/components/common/UserCard/UserCardComponent";

import { LoadingBar } from "@appbase/components/loading";

@Component({
    components: {
        AttachFileField,
        CommentBox,
        RecommendList,
        TagField,
        PasswordCheck,
        LoadingBar,
        ArticleHistoryList,
        ViewFrame,
    },

    computed: mapGetters({
        user: accountTypes.USER,
        current: accountTypes.CURRENT,
        popupBaseUrl: types.POPUP_BASEURL,
    }),

    methods: mapActions({
        fetchReadConfig: types.FETCH_READCONFIG,
        readArticle: types.READ_ARTICLE,
        createViewLog: types.CREATE_VIEWLOG,
        createRecommend: types.CREATE_RECOMMEND,
        deleteRecommend: types.DELETE_RECOMMEND,
        deleteArticle: types.DELETE_ARTICLE,
    }),
})
export default class BoardRead extends UserCardComponent {
    @Prop({ type: Boolean, default: false }) isModal!: boolean;
    @Prop({ type: Object }) webpartParam!: board.PreviewSet | undefined;

    fetchReadConfig!: (boardId: number) => Promise<board.ReadConfig>;
    readArticle!: (articleSet: article.ArticleFetchSet) => Promise<article.ArticleDetail>;
    createViewLog!: (set: article.CreateOptionSet) => Promise<number>;
    createRecommend!: (set: article.CreateOptionSet) => Promise<number>;
    deleteRecommend!: (set: article.DeleteRecommendSet) => Promise<number>;
    deleteArticle!: (set: article.DeleteArticle) => Promise<number>;

    user!: account.User;
    current!: account.TCurrent;
    popupBaseUrl!: string;
    userLang: string = "";

    fieldLoaded: boolean = false;
    photoLoaded: boolean = false;

    currentBoard: number = 0;
    currentArticle: number = 0;
    currentLookahead: string | null = null;
    readConfig: board.ReadConfig | null = null;
    contentFields: board.ContentField[] = [];
    componentValues: (Object | null)[] = [];
    currentCheckingFor: "edit" | "delete" = "edit";

    mBoardName: string = "";
    mUserPhoto: string = "";
    mArticle: article.ArticleDetail | null = null;
    mViewCount: number = 0;
    mCommentCount: number = 0;
    mRecommendCount: number = 0;
    mHasRecommended: boolean = false;
    mFileCode: string | null = null;
    mTags: string = "";
    mShowPasswordCheck: boolean = false;
    mCreatedDate: string = "";
    mCreatedTime: string = "";

    mIsUpdated: boolean = false;
    mUpdatedUser: article.ArticleUserInfo | null = null;
    mUpdatedDate: string = "";
    mUpdatedTime: string = "";
    mUpdatedString: string = "";

    mShowComment: boolean = true;
    mShowRecommend: boolean = false;
    mUseReply: boolean = false;
    mUseComment: boolean = false;
    mUseRecommend: boolean = false;
    mPrintMode: boolean = false;
    mLookahead: boolean = false;
    mUseTag: boolean = false;
    mUseNameless: boolean = false;
    mUseArticleHistory: boolean = false;
    mUseContent: boolean = false;
    mBodyUrl: string = "";

    currentArticleVersion: number = 0;
    showArticleHistory: boolean = false;
    articleHistoryList: article.ArticleHistoryItem[] = [];
    systemAuth: article.SystemAuth[] = [];
    canUpdate: boolean = false;
    canDelete: boolean = false;
    canReply: boolean = false;

    showHeader: boolean = true;
    showButtons: boolean = true;

    mUserInfo: article.ArticleUserInfo = {
        PersonCode: "",
        DisplayName: "",
        DisplayNameEn: "",
        DisplayNameCh: "",
        DisplayNameJp: "",
        DisplayDeptName: "",
        DisplayDeptNameEn: "",
        DisplayDeptNameCh: "",
        DisplayDeptNameJp: "",
        TitleName: "",
        TitleNameEn: "",
        TitleNameCh: "",
        TitleNameJp: "",
    };

    synapopts: editor.synap.SynapEditorConfig = {
        "editor.autoSave": false,
        "editor.autoSave.period": 10,
        "editor.menu.show": false,
        "editor.size.height": "100%",
    };

    get isInFrame() {
        try {
            return window.top !== window.self;
        } catch (e) {
            return true;
        }
    }

    @Watch("webpartParam")
    onWebpartParamChanged(newVal: board.PreviewSet) {
        this.fieldLoaded = false;
        this.currentArticle = newVal.articleId ?? 0;
        this.currentBoard = newVal.boardId;

        this.mountAction();
    }

    created() {
        this.userLang = this.current.userConfig.language; // 현재 사용자의 언어설정 정보
        if (this.webpartParam) {
            this.currentArticle = this.webpartParam.articleId ?? 0;
            this.currentBoard = this.webpartParam.boardId;
            if (this.webpartParam.lookahead) this.currentLookahead = this.webpartParam.lookahead.toString();

            this.showHeader = this.webpartParam.showHeader ?? false;
            this.showButtons = this.webpartParam.showButtons ?? false;
        } else {
            var { SYSID, LOOKAHEAD, ARTICLEID, TEAMSPRINT } = this.$route.query;

            if (SYSID) this.currentBoard = Number(SYSID);
            if (ARTICLEID) this.currentArticle = Number(ARTICLEID);
            if (LOOKAHEAD) this.currentLookahead = LOOKAHEAD.toString();
            if (TEAMSPRINT && TEAMSPRINT === "true") this.mPrintMode = true;
        }

        this.mountAction();
    }

    mountAction() {
        var mountPromise: Promise<any>[] = [];
        var lookaheadArticle: article.ArticleDetail;

        mountPromise.push(api.board.getReadConfig(this.currentBoard));
        mountPromise.push(api.article.getSystemAuth(this.currentBoard));

        if (this.currentLookahead == null) {
            mountPromise.push(this.articleHistoryDetail());
        } else {
            var mLookahead = localStorageHelper.get(`$board/${this.currentLookahead}/lookahead`);
            localStorageHelper.set(`$board/${this.currentLookahead}/lookahead`, null);

            lookaheadArticle = JSON.parse(mLookahead) as article.ArticleDetail;

            if (lookaheadArticle.printMode) {
                this.mPrintMode = lookaheadArticle.printMode;
            } else {
                this.mLookahead = true;
            }

            this.mArticle = lookaheadArticle;
            this.mFileCode = lookaheadArticle.fileCode;
        }

        Promise.all(mountPromise).then(async (res) => {
            this.readConfig = res[0] as board.ReadConfig;
            this.systemAuth = res[1];
            var articleModel = this.currentLookahead == null ? (res[2] as article.ArticleDetail) : lookaheadArticle;

            this.mBoardName = this.getLangProperty(this.readConfig, "boardName");

            this.getUserPhoto(articleModel.createdUser);
            this.mUseComment = this.readConfig.useComment;
            this.mUseRecommend = this.readConfig.useRecommend;
            this.mUseReply = this.readConfig.useReply;
            this.mUseTag = this.readConfig.useTag;
            this.mUseNameless = this.readConfig.useNameless;
            this.mUseArticleHistory = this.readConfig.useArticleHistory;
            this.mUseContent = this.readConfig.useContent;
            this.contentFields = this.readConfig.contentFields.filter((f) => ids().contentFieldFilter(f));

            if (this.currentLookahead != null || this.checkPermission(this.readConfig, articleModel, this.systemAuth)) {
                this.loadArticleDetail(articleModel);
                // yaskawa 요청 반영
                if(this.mArticle?.isDraft){
                    this.canReply = false;
                }
                let contentUrl = invalidateUrl(`/cm/Portal/Article/view/Content`);
                this.mBodyUrl = `${contentUrl}?boardId=${this.currentBoard}&articleId=${this.currentArticle}&articleVersion=${this.currentArticleVersion}`;
            } else {
                await (window as any).alertAsync(this.$t("게시물_조회권한_없음"));
                this.onCloseClick();
            }
        });
    }

    onLookaheadLoaded() {
        if (this.mPrintMode) {
            var frm = document.querySelector('iframe') as HTMLIFrameElement
            if(frm.contentWindow){
                frm.outerHTML = frm.contentWindow.document.body.innerHTML
            }
            window.print();
            setTimeout(function () {
                window?.close();
            }, 1);
        }
    }

    getUserPhoto(personCode: string) {
        cache.avatar.get(personCode).then((url) => {
            if (url && url != null) {
                this.mUserPhoto = url;
            } else {
                this.mUserPhoto = "/cm/gres/images/ghost";
            }

            this.photoLoaded = true;
        });
    }

    getFieldComponent(field: board.ContentField) {
        switch (field.fieldType) {
            case "TEXT":
                return TextField;
            case "TEXTAREA":
                return TextAreaField;
            case "DATE":
                return DateField;
            case "SELECT":
                return SelectField;
            case "TIMESPAN":
                return TimeSpanField;
            case "MAILSEND":
                return MailSendField;
            case "SELECTUSER":
                return SelectUserField;
            case "OPENWINDOW":
                return OpenWindowField;
            case "DATETIME-START":
            case "DATETIME-END":
            case "DATETIME":
                return DatetimeField;
        }
    }

    checkWriteRights(readConfig: board.ReadConfig, systemAuth: article.SystemAuth[]): boolean {
        var superAdmin: boolean = this.current.permissions.isSuperAdmin;
        var companyAdmin: boolean = this.current.permissions.isCompanyManager && readConfig.comCode === this.user.comCode;
        var boardAdmin: boolean = readConfig.boardManagers.findIndex((m) => m.manager === this.user.personCode) >= 0;
        var menuAdmin: boolean = systemAuth?.findIndex((a) => a.systemId === this.currentBoard && a.isMenuAdmin) >= 0;

        if (superAdmin || companyAdmin || boardAdmin || menuAdmin) {
            return true;
        } else {
            if (readConfig.permission.roleName !== "Default") {
                return readConfig.permission.write;
            } else {
                var currentBoardAuths = systemAuth?.filter((a) => a.systemId === this.currentBoard) || [];
                console.log("currentAuth", currentBoardAuths);

                return currentBoardAuths.some((a) => a.write);
            }
        }
    }

    checkPermission(readConfig: board.ReadConfig, article: article.ArticleDetail, auth: article.SystemAuth[]): boolean {
        var readable = false;
        var superAdmin: boolean = this.current.permissions.isSuperAdmin;
        var companyAdmin: boolean = this.current.permissions.isCompanyManager && readConfig.comCode === this.user.comCode;
        var boardAdmin = readConfig.boardManagers.findIndex((m) => m.manager === this.user.personCode) >= 0;
        var menuAdmin = auth?.findIndex((a) => a.systemId == this.currentBoard && a.isMenuAdmin) >= 0;
        var currentBoardAuths = auth?.filter((a) => a.systemId === this.currentBoard) || [];
        console.log("currentAuth", currentBoardAuths);

        if (readConfig.boardType === "N" || readConfig.boardType === "R") {
            if (superAdmin || companyAdmin || boardAdmin || menuAdmin) {
                readable = true;
            } else {
                if (readConfig.permission.roleName !== "Default") {
                    readable = readConfig.permission.read;
                } else {
                    readable = currentBoardAuths.some((a) => a.read);
                }
            }
        } else {
            readable =
                superAdmin ||
                companyAdmin ||
                boardAdmin ||
                menuAdmin ||
                this.user.personCode === article.createdUser ||
                this.user.personCode === article.parentArticleUser;
        }

        if (readable) {
            if (superAdmin || companyAdmin || boardAdmin || menuAdmin) {
                this.canUpdate = true;
                this.canDelete = true;
                this.canReply = true;
            } else if (this.readConfig?.boardType === "R") {
                this.canUpdate = false;
                this.canDelete = false;
                this.canReply = this.checkWriteRights(readConfig, auth);
            } else if (this.user.personCode === article.createdUser) {
                this.canUpdate = true;
                this.canDelete = true;
                this.canReply = this.checkWriteRights(readConfig, auth);
            } else {
                this.canUpdate = currentBoardAuths.some((a) => a.update);
                this.canDelete = currentBoardAuths.some((a) => a.delete);
                this.canReply = this.checkWriteRights(readConfig, auth);
            }
        }

        return readable;
    }

    loadArticleDetail(article: article.ArticleDetail) {
        this.mountUserInfo(article);

        this.mArticle = article;
        this.mViewCount = article.viewCount;

        if (this.currentArticleVersion == 0) {
            this.mCommentCount = article.commentCount;
            this.mRecommendCount = article.recommendCount;
            this.mHasRecommended = article.recommendedDate != null;
        }

        this.mFileCode = article.fileCode;
        this.mTags = article.tags;
        this.mUseNameless = this.mUseNameless || article.isNameless;

        this.fillComponentValues(article);
        this.fieldLoaded = true;
        
        var eventTarget = this.isModal || (this.webpartParam && this.showButtons) ? window : window.opener;

        if(!this.isTeams && !this.webpartParam && this.readConfig?.usePopup){
            dispatchEventBus(eventTarget, "boardlist-reload", "");
        }

        this.$emit("articleLoaded", this.readConfig, this.mArticle);

        if (this.currentLookahead == null) {
            this.createViewLog({
                articleId: this.currentArticle,
                userCode: this.user.personCode,
                userName: this.user.displayName,
                userName_En: this.user.displayDeptNameEn,
                userName_Ch: this.user.displayDeptNameCh,
                userName_Jp: this.user.displayDeptNameJp,
                userMail: this.user.email,
                userDept: this.user.deptCode,
                userPhone: this.user.officeTel,
                deptName: this.user.deptName,
                deptName_En: this.user.deptNameEn,
                deptName_Ch: this.user.deptNameCh,
                deptName_Jp: this.user.deptNameJp,
                userJobTitle: this.user.titleName,
                userJobTitle_En: this.user.titleNameEn,
                userJobTitle_Ch: this.user.titleNameCh,
                userJobTitle_Jp: this.user.titleNameJp,
            });
        }
    }

    mountUserInfo(article: article.ArticleDetail) {
        this.mUserInfo = article.createdUserInfo;
        this.mCreatedDate = uGetDateFormat("yyyy-MM-dd", IEDate(article.createdDate));
        this.mCreatedTime = uGetDateFormat("H:i:s", IEDate(article.createdDate));

        if (this.mUserInfo == null) {
            this.mUserInfo = {
                PersonCode: article.createdUser,
                DisplayName: article.createdUserName,
                DisplayNameEn: article.createdUserName_En,
                DisplayNameCh: article.createdUserName_Ch,
                DisplayNameJp: article.createdUserName_Jp,
                DisplayDeptName: article.createdDeptName,
                DisplayDeptNameEn: article.createdDeptName_En,
                DisplayDeptNameCh: article.createdDeptName_Ch,
                DisplayDeptNameJp: article.createdDeptName_Jp,
                TitleName: article.createdUserJobTitle,
                TitleNameEn: article.createdUserJobTItle_En,
                TitleNameCh: article.createdUserJobTItle_Ch,
                TitleNameJp: article.createdUserJobTItle_Jp,
            };
        }

        if (article.articleVersion <= 1) {
            this.mIsUpdated = false;
            this.mCreatedDate = uGetDateFormat("yyyy-MM-dd", IEDate(article.updatedDate));
            this.mCreatedTime = uGetDateFormat("H:i:s", IEDate(article.updatedDate));
            return;
        }

        if (
            article.updatedDate != null &&
            (uGetDateFormat("yyyyMMddHis", IEDate(article.createdDate)) !== uGetDateFormat("yyyyMMddHis", IEDate(article.updatedDate)) ||
                article.createdUser !== article.updatedUser)
        ) {
            this.mIsUpdated = true;
            this.mUpdatedDate = uGetDateFormat("yyyy-MM-dd", IEDate(article.updatedDate));
            this.mUpdatedTime = uGetDateFormat("H:i:s", IEDate(article.updatedDate));

            this.mUpdatedUser = {
                PersonCode: article.updatedUser ?? "",
                DisplayName: article.updatedUserName ?? "",
                DisplayNameEn: article.updatedUserName_En ?? "",
                DisplayNameJp: article.updatedUserName_Jp ?? "",
                DisplayNameCh: article.updatedUserName_Ch ?? "",
                DisplayDeptName: article.updatedDeptName ?? "",
                DisplayDeptNameEn: article.updatedDeptName_En ?? "",
                DisplayDeptNameCh: article.updatedDeptName_Ch ?? "",
                DisplayDeptNameJp: article.updatedDeptName_Jp ?? "",
                TitleName: article.updatedUserJobTitle ?? "",
                TitleNameEn: article.updatedUserJobTitle_En ?? "",
                TitleNameCh: article.updatedUserJobTitle_Ch ?? "",
                TitleNameJp: article.updatedUserJobTitle_Jp ?? "",
            };

            let updatedUser = `${this.getLangProperty(this.mUpdatedUser, "DisplayName")} ${this.getLangProperty(this.mUpdatedUser, "TitleName")}`;
            this.mUpdatedString = this.$t("마지막으로_수정함").toString().replace("{USER_STRING}", updatedUser);
        }
    }

    fillComponentValues(article: article.ArticleDetail) {
        var passVal: Object | undefined | null = null;
        this.contentFields.forEach((c) => {
            var find = article.fieldValues.find((f) => f.fieldId == c.fieldId);
            passVal = find?.value;

            if (passVal) {
                var type = (typeof passVal).toString();
                passVal = type !== "object" ? passVal.toString() : passVal;
                this.componentValues.push(passVal);
            } else {
                this.componentValues.push(null);
            }
        });
    }

    toggleShowComment() {
        this.mShowComment = !this.mShowComment;

        if (this.mShowComment) {
            this.mShowRecommend = false;
        }
    }

    toggleRecommend() {
        this.mShowRecommend = !this.mShowRecommend;

        if (this.mShowRecommend) {
            this.mShowComment = false;
        }
    }

    recommendArticle() {
        var userset = <article.CreateOptionSet>{
            articleId: this.currentArticle,
            deptName: this.user.deptName,
            deptName_En: this.user.deptNameEn,
            deptName_Ch: this.user.deptNameCh,
            deptName_Jp: this.user.deptNameJp,
            userCode: this.user.personCode,
            userJobTitle: this.user.titleName,
            userJobTitle_En: this.user.titleNameEn,
            userJobTitle_Ch: this.user.titleNameCh,
            userJobTitle_Jp: this.user.titleNameJp,
            userName: this.user.displayName,
            userName_En: this.user.displayNameEn,
            userName_Ch: this.user.displayNameCh,
            userName_Jp: this.user.displayNameJp,
        };

        var recommendPromise = this.mHasRecommended ? this.deleteRecommend(userset) : this.createRecommend(userset);

        recommendPromise.then((res) => {
            this.mHasRecommended = !this.mHasRecommended;
        });
    }

    onCommentMounted(comments: article.ArticleComment[], count: number) {
        this.mCommentCount = count;
    }

    onRecommendMounted(recommends: article.RecommendSet[], count: number) {
        this.mRecommendCount = count;
    }

    async onPasswordCheckConfirm(password: string) {
        if (this.currentCheckingFor === "edit") {
            var aes = new VueAES();
            var encrypt = aes.encrypt(password);
            localStorageHelper.set(`eumboard-nameless-article-${this.currentArticle}`, encrypt);

            this.handleEdit();
        } else if (this.currentCheckingFor === "delete") {
            await this.handleDelete();
        }
    }

    onPasswordCheckCancel() {
        this.mShowPasswordCheck = false;
    }

    onEditClick() {
        if (this.mUseNameless) {
            this.currentCheckingFor = "edit";
            this.mShowPasswordCheck = true;
        } else {
            this.handleEdit();
        }
    }

    async onDeleteClick() {
        if (this.mUseNameless) {
            this.currentCheckingFor = "delete";
            this.mShowPasswordCheck = true;
        } else {
            await this.handleDelete();
        }
    }

    handleEdit() {
        if (this.webpartParam && this.showButtons) {
            this.$emit("edit", this.mArticle);
        } else {
            open(OpenType.ModalEdit, {
                boardId: this.currentBoard,
                articleId: this.currentArticle,
                baseUrl: this.popupBaseUrl,
                query: { MENUID: (this.$route.query.MENUID as string) ?? "" },
            });
        }
    }

    async handleDelete() {
        let deleteMessage: string = this.$t("M_BB_DeleteMessage") as string;
        var returnMessage = await (window as any).confirmAsync(deleteMessage);

        if (returnMessage) {
            this.deleteArticle(<article.DeleteArticle>{
                articleId: this.currentArticle,
                deleteUser: {
                    userCode: this.user.personCode,
                    userName: this.user.displayName,
                    userName_En: this.user.displayDeptNameEn,
                    userName_Ch: this.user.displayDeptNameCh,
                    userName_Jp: this.user.displayDeptNameJp,
                    deptName: this.user.titleName,
                    deptName_En: this.user.titleNameEn,
                    deptName_Ch: this.user.titleNameCh,
                    deptName_Jp: this.user.titleNameJp,
                    userJobTitle: this.user.deptName,
                    userJobTitle_En: this.user.deptNameEn,
                    userJobTitle_Ch: this.user.deptNameCh,
                    userJobTitle_Jp: this.user.deptNameJp,
                },
            }).then((res) => {
                // 게시글 삭제 완료 이벤트 발생 (N 배지 갱신용)
                if (this.currentBoard) {
                    // localStorage를 통한 크로스 윈도우 이벤트 전달
                    const eventData = {
                        type: 'article-deleted',
                        boardId: this.currentBoard,
                        timestamp: Date.now()
                    };
                    localStorage.setItem('board-event', JSON.stringify(eventData));

                    // localStorage 이벤트를 즉시 제거 (다음 이벤트를 위해)
                    setTimeout(() => {
                        localStorage.removeItem('board-event');
                    }, 100);
                }

                var target = this.isModal || (this.webpartParam && this.showButtons) ? window : window.opener;
                dispatchEventBus(target, "boardlist-reload", "");
                this.onCloseClick();
            });
        }
    }

    getReplyHeadRawHtml(article: article.ArticleDetail | null): string {
        if (article == null) return "";
        let lblWriter = this.$t("작성자").toString();
        let lblCreateDate = this.$t("작성일").toString();
        let lblSubject = this.$t("제목").toString();
        var returnHtml = `
        <p>&nbsp;</p>
        <hr style="width: 98%; display: inline-block;" tabindex="-1" />
        <div id="divRplyFwdMsg" dir="ltr">    
            <span style="font-size: 10pt; color: #000000; font-family: dotum;"
                data-mce-style="font-size: 10pt; color: #000000; font-family: dotum;">`;
        returnHtml += `
                <strong>${lblWriter}:</strong> ${article.createdUserName} / ${article.createdUserJobTitle} / ${article.createdDeptName}<br>
                <strong>${lblCreateDate}:</strong> ${article.createdDate} <br>`;
        returnHtml += `
                <strong>${lblSubject}:</strong> ${article.title}</span>
            <div>&nbsp;</div>
        </div>`;

        return returnHtml;
    };

    onReplyClick() {
        if (this.readConfig == null || this.mArticle == null) return;

        var dispContent = `${this.getReplyHeadRawHtml(this.mArticle)}${this.mArticle?.content}`;

        var replyMetadata: board.ReplyMetadata = {
            prefaceId: this.mArticle.prefaceId,
            title: this.mArticle.title,
            content: dispContent,
        };

        localStorageHelper.set(`$board/${this.currentArticle}/reply`, replyMetadata);
        var openType: OpenType = OpenType.PopupReply;

        if (this.webpartParam && this.showButtons) {
            this.$emit("reply", this.mArticle);
        } else {
            if (this.isTeams || !this.readConfig.usePopup) {
                openType = OpenType.ModalReply;
            }

            open(openType, {
                boardId: this.readConfig.boardId,
                parentArticle: this.currentArticle,
                baseUrl: this.popupBaseUrl,
                query: { MENUID: (this.$route.query.MENUID as string) ?? "" },
            });
        }
    }

    onPrintClick() {
        if (this.mArticle != null) {
            if (this.isTeams || this.isModal) {
                var printFrame = this.$refs.iPrintFrame as HTMLIFrameElement;
                printFrame.src = `${window.location.origin}${this.popupBaseUrl}readPopup?SYSID=${this.currentBoard}&ARTICLEID=${this.mArticle.articleId}&TEAMSPRINT=true`;
            } else {
                var lookaheadId = getGUID(false);
                var mlookahead: article.ArticleDetail = {
                    ...this.mArticle,
                    printMode: true,
                };

                var strArticle = JSON.stringify(mlookahead);
                localStorageHelper.set(`$board/${lookaheadId}/lookahead`, strArticle);

                openPopup(
                    "",
                    `readPopup?SYSID=${this.currentBoard}&LOOKAHEAD=${lookaheadId}`,
                    {},
                    {
                        width: 900,
                        height: 760,
                        resizable: 1,
                    }
                );
            }
        }
    }

    onCloseClick() {
        if (this.isModal) {
            this.onCloseModal();
        } else {
            if (this.webpartParam && this.showButtons) {
                this.$emit("close");
            } else {
                window.close();
            }
        }
    }

    onCloseModal() {
        var closePath = this.$route.path.replace("/modal/read", "");
        var menuId = this.$route.query.MENUID ? this.$route.query.MENUID.toString() : "";

        if(this.isTeams && this.readConfig?.usePopup){
            dispatchEventBus(window, "boardlist-reload", "");
        }
        this.$router.push({
            path: closePath,
            query: {
                SYSID: this.currentBoard.toString(),
                MENUID: menuId,
                PRESERVEVIEW: "1",
            },
        });
    }

    onHistoryClick() {
        api.article.getArticleHistoryList(this.currentArticle).then((res) => {
            if (res) {
                this.articleHistoryList = res;
                this.showArticleHistory = true;
            }
        });
    }

    onCloseHistory() {
        this.articleHistoryList = [];
        this.showArticleHistory = false;
    }

    onHistoryItemClick(articleVersion: number, fileCode: string) {
        this.currentArticleVersion = articleVersion;
        this.mFileCode = fileCode;
        this.onCloseHistory();
        this.mountAction();
    }

    articleHistoryDetail(): Promise<article.ArticleDetail> {
        return api.article.getArticleDetailHistory(this.currentBoard, this.currentArticle, this.currentArticleVersion);
    }

    applyTableWrapperToDOM() {
        const viewFrameComponent = this.$refs.viewFrameContent as any;
        if (!viewFrameComponent) return;

        try {
            const iframe = viewFrameComponent.$el as HTMLIFrameElement;
            if (iframe && iframe.contentDocument) {
                const tables = iframe.contentDocument.querySelectorAll('table');
                tables.forEach((table) => {
                    if (table.parentElement?.style.overflowX === 'auto') return;
                    
                    let wrapper = document.createElement('div');
                    wrapper.style.overflowX = 'auto';
                    wrapper.style.maxWidth = '100%';
                    
                    table.parentNode?.replaceChild(wrapper, table);
                    wrapper.appendChild(table);
                });
            }
        } catch (e) {
            // 무시
        }
    }
}
